'use client';

import { FC, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useFormContext } from 'react-hook-form';
import { useTranslations } from '@/hooks/useTranslations';
import { getCurrencyFormatter } from '@/lib/currency-formatter';
import { TCurrency } from '@/types';
import { TItems } from '@/types/invoice';
import { DEFAULT_EXPENSE_ITEM } from '@/lib/constants/expense';
import { deriveTaxAmountFromTotalWithVat } from '@/lib/helpers/expense-math';
import { Plus, Trash2 } from 'lucide-react';

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

interface IExpenseItems {
  status: SubmitStatus;
  items: TItems[];
  setItems: (items: TItems[]) => void;
  currency?: TCurrency;
}

export const ExpenseItems: FC<IExpenseItems> = ({
  items,
  setItems,
  status,
  currency,
}) => {
  const t = useTranslations('CreateExpensePopup');
  const { setValue } = useFormContext();

  useEffect(() => {
    setValue('items', items);
  }, [items, setValue]);

  const addRow = () => {
    setItems([...items, { ...DEFAULT_EXPENSE_ITEM }]);
  };

  const removeRow = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItemField = (
    index: number,
    field: string,
    value: string | number
  ) => {
    const updatedItems = items.map((item, i) => {
      if (i !== index) return item;

      const updatedItem = { ...item, [field]: value };
      const numVal = typeof value === 'number' ? value : Number(value);

      if (field === 'qty' || field === 'price') {
        const qty = field === 'qty' ? (isNaN(numVal) ? 0 : numVal) : item.qty;
        const price =
          field === 'price' ? (isNaN(numVal) ? 0 : numVal) : item.price;
        updatedItem.total = Number(qty) * Number(price);
        if (updatedItem.total > 0 && item.tax > 0) {
          updatedItem.taxAmount = deriveTaxAmountFromTotalWithVat(
            updatedItem.total,
            item.tax
          );
        }
      } else if (field === 'tax') {
        if (updatedItem.total > 0 && numVal > 0) {
          updatedItem.taxAmount = deriveTaxAmountFromTotalWithVat(
            updatedItem.total,
            numVal
          );
        }
      }
      // taxAmount: user enters directly, no derivation

      return updatedItem;
    });

    setItems(updatedItems);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <FormLabel>{t('items')}</FormLabel>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addRow}
          disabled={status === 'loading'}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('addItem')}
        </Button>
      </div>

      {items.map((item, index) => (
        <div
          key={index}
          className="flex flex-wrap items-start justify-between gap-3 w-full p-4 border rounded-lg"
        >
          <FormField
            name={`items[${index}].description`}
            render={() => (
              <FormItem className="w-full">
                <FormLabel>{t('itemDescription')}</FormLabel>
                <FormControl>
                  <Input
                    value={item.description}
                    onChange={(e) =>
                      updateItemField(index, 'description', e.target.value)
                    }
                    placeholder={t('itemDescription')}
                    disabled={status === 'loading'}
                    type="text"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-between gap-2 w-full">
            <FormField
              name={`items[${index}].qty`}
              render={() => (
                <FormItem className="w-1/4">
                  <FormLabel>{t('quantity')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.qty || ''}
                      disabled={status === 'loading'}
                      onChange={(e) => {
                        const value = e.target.valueAsNumber;
                        updateItemField(index, 'qty', isNaN(value) ? 0 : value);
                      }}
                      placeholder={t('quantity')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name={`items[${index}].unit`}
              render={() => (
                <FormItem className="w-1/4">
                  <FormLabel>{t('unit')}</FormLabel>
                  <FormControl>
                    <Input
                      value={item.unit}
                      onChange={(e) =>
                        updateItemField(index, 'unit', e.target.value)
                      }
                      placeholder={t('unit')}
                      disabled={status === 'loading'}
                      type="text"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name={`items[${index}].price`}
              render={() => (
                <FormItem className="w-1/4">
                  <FormLabel>{t('priceWithVat')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.price || ''}
                      onChange={(e) => {
                        const value = e.target.valueAsNumber;
                        updateItemField(
                          index,
                          'price',
                          isNaN(value) ? 0 : value
                        );
                      }}
                      disabled={status === 'loading'}
                      placeholder={t('price')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name={`items[${index}].tax`}
              render={() => (
                <FormItem className="w-1/4">
                  <FormLabel>{t('tax')} (%)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.tax || ''}
                      onChange={(e) => {
                        const value = e.target.valueAsNumber;
                        updateItemField(index, 'tax', isNaN(value) ? 0 : value);
                      }}
                      disabled={status === 'loading'}
                      placeholder={t('tax')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-between gap-2 w-full">
            <FormField
              name={`items[${index}].taxAmount`}
              render={() => (
                <FormItem className="w-1/2">
                  <FormLabel>{t('itemTaxAmount')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.taxAmount ?? ''}
                      onChange={(e) => {
                        const value = e.target.valueAsNumber;
                        updateItemField(
                          index,
                          'taxAmount',
                          isNaN(value) ? 0 : value
                        );
                      }}
                      disabled={status === 'loading'}
                      placeholder={t('itemTaxAmountPlaceholder')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-between items-center w-full">
            <FormField
              name={`items[${index}].total`}
              render={() => (
                <FormItem className="flex-1">
                  <FormLabel>{t('total')}</FormLabel>
                  <FormControl>
                    <Input
                      value={getCurrencyFormatter(item.total, currency)}
                      disabled={true}
                      placeholder={t('total')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => removeRow(index)}
              disabled={status === 'loading' || items.length === 1}
              className="ml-4"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};
